/**
 * Fonte exportável: Ordens de Serviço (listagem oficial)
 * -----------------------------------------------------------------------------
 * Reaproveita `get_service_orders_paginated` com `p_export_mode = true`, que
 * valida a permissão (assert_export_allowed 'service_orders') e o limite de uso,
 * liberando blocos de até 500 linhas.
 *
 * RELEASE: 2026-10-08 v1.0.0 - Criação inicial (pendência da Fase 5 OS).
 */
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { SERVICE_ORDER_PRIORITIES, SERVICE_ORDER_STATUS_LABEL, type ServiceOrderRow, type ServiceOrdersFilters } from '@/hooks/useServiceOrders';
import { EXPORT_ROW_LIMIT, type ExportSource } from '../types';

const logger = createLogger('ExportServiceOrders');
const LOTE = 500;
const rpc = (fn: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (f: string, a: Record<string, unknown>) => Promise<{ data: any; error: any }>).call(supabase, fn, args);

export type ServiceOrdersExportParams = Omit<ServiceOrdersFilters, 'page' | 'pageSize'>;

export const serviceOrdersExportSource: ExportSource<ServiceOrdersExportParams> = {
  id: 'service_orders',
  label: 'Ordens de Serviço',
  moduleKey: 'ordem_servico',
  fileBaseName: 'ordens_servico',
  columns: [
    { key: 'numero', label: 'Número', group: 'OS', defaultSelected: true },
    { key: 'situacao', label: 'Situação', group: 'OS', defaultSelected: true },
    { key: 'prioridade', label: 'Prioridade', group: 'OS', defaultSelected: true },
    { key: 'tipo', label: 'Tipo de serviço', group: 'OS', defaultSelected: true },
    { key: 'fase', label: 'Fase', group: 'OS', defaultSelected: true },
    { key: 'prestador', label: 'Prestador', group: 'OS', defaultSelected: true },
    { key: 'cliente', label: 'Cliente', group: 'Cliente', defaultSelected: true },
    { key: 'placa', label: 'Placa', group: 'Cliente', defaultSelected: true },
    { key: 'veiculo', label: 'Veículo', group: 'Cliente', defaultSelected: false },
    { key: 'cidade', label: 'Cidade/UF', group: 'Cliente', defaultSelected: false },
    { key: 'inicio', label: 'Início agendado', group: 'Agenda', kind: 'datetime', defaultSelected: true },
    { key: 'fim', label: 'Fim agendado', group: 'Agenda', kind: 'datetime', defaultSelected: false },
    { key: 'atrasada', label: 'Atrasada', group: 'Agenda', defaultSelected: true },
    { key: 'criada', label: 'Criada em', group: 'OS', kind: 'datetime', defaultSelected: true },
  ],
  fetchRows: async (tenantId, p) => {
    const out: ServiceOrderRow[] = [];
    for (let page = 1; out.length < EXPORT_ROW_LIMIT; page++) {
      const { data, error } = await rpc('get_service_orders_paginated', {
        p_search: p.search || null, p_status: p.status ?? null,
        p_service_type_id: p.serviceTypeId, p_provider_person_id: p.providerId,
        p_date_from: p.dateFrom, p_date_to: p.dateTo,
        p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
        p_sort_field: p.sortField, p_sort_direction: p.sortDirection,
        p_page: page, p_page_size: LOTE, p_export_mode: true, p_tenant_hint: tenantId,
      });
      if (error) { logger.error('Falha na exportação de OS', { error: error.message }); throw new Error(error.message); }
      const items = (data?.items ?? []) as ServiceOrderRow[];
      out.push(...items);
      if (items.length < LOTE) break;
    }
    return out.slice(0, EXPORT_ROW_LIMIT).map((o) => ({
      numero: o.number, situacao: SERVICE_ORDER_STATUS_LABEL[o.status] ?? o.status,
      prioridade: SERVICE_ORDER_PRIORITIES.find((x) => x.value === o.priority)?.label ?? o.priority,
      tipo: o.service_type_name, fase: o.phase_name, prestador: o.provider_name, cliente: o.customer_name,
      placa: o.vehicle_plate, veiculo: o.vehicle_label,
      cidade: [o.address_city, o.address_state].filter(Boolean).join('/') || null,
      inicio: o.scheduled_start, fim: o.scheduled_end, atrasada: o.is_overdue ? 'Sim' : 'Não', criada: o.created_at,
    }));
  },
};
